import { ref } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

/** Optional call-to-action rendered as a button in the toast (dead ends should offer a way forward). */
export interface ToastAction {
  label: string
  /** In-app destination (router.push). */
  to?: RouteLocationRaw
  onClick?: () => void
}

export interface Toast {
  id: string
  title?: string
  description?: string
  variant?: 'default' | 'destructive'
  duration?: number
  action?: ToastAction
}

const toasts = ref<Toast[]>([])

function generateId() {
  return Math.random().toString(36).substring(2, 9)
}

export function useToast() {
  function toast(options: Omit<Toast, 'id'>) {
    const id = generateId()
    const newToast: Toast = {
      id,
      // Toasts with an action stay up longer so there is time to click it.
      duration: options.action ? 9000 : 5000,
      variant: 'default',
      ...options
    }

    toasts.value.push(newToast)

    // Auto dismiss
    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        dismiss(id)
      }, newToast.duration)
    }

    return id
  }

  function dismiss(id: string) {
    const index = toasts.value.findIndex(t => t.id === id)
    if (index > -1) {
      toasts.value.splice(index, 1)
    }
  }

  return {
    toasts,
    toast,
    dismiss
  }
}
